import React from 'react';
import ParticlesBG from '../../Shared/ParticlesBG';
import '../../Assets/Stylesheets/Pages/Legal/Legal.scss';

export default function RefundPolicy() {
  const lastUpdated = "January 20, 2026";
  const companyName = "TRND";
  const companyEmail = "support@trnd.co";

  return (
    <div className="legal-page">
      <ParticlesBG />
      <div className="legal-content">
        <h1>Refund Policy</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section className="legal-section">
          <h2>1. Overview</h2>
          <p>
            At {companyName}, we strive to provide excellent service and value to our 
            customers. This Refund Policy outlines when and how you may be eligible for 
            refunds on our various products and services.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Subscription Refunds</h2>
          
          <h3>2.1 Free Trial</h3>
          <p>
            If a free trial is offered, you will not be charged during the trial period. 
            You may cancel at any time during the trial without being charged.
          </p>

          <h3>2.2 Monthly Subscriptions</h3>
          <ul>
            <li>Monthly subscriptions are billed in advance</li>
            <li>Refunds are generally not provided for partial months</li>
            <li>You may cancel at any time, and your access will continue until the end of the current billing period</li>
            <li>Within the first 14 days of a new subscription, you may request a full refund if you have not significantly used the service</li>
          </ul>

          <h3>2.3 Annual Subscriptions</h3>
          <ul>
            <li>Annual subscriptions are billed in advance for the full year</li>
            <li>Within the first 30 days, you may request a full refund if you have not significantly used the service</li>
            <li>After 30 days, prorated refunds may be considered on a case-by-case basis for extenuating circumstances</li>
            <li>Early cancellation of annual plans does not entitle you to a prorated refund unless otherwise specified</li>
          </ul>

          <h3>2.4 Subscription Downgrades</h3>
          <p>
            If you downgrade from a higher tier to a lower tier:
          </p>
          <ul>
            <li>The change will take effect at your next billing cycle</li>
            <li>No partial refunds are provided for the difference in pricing</li>
            <li>You will retain access to the higher tier features until the current period ends</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Token and Usage Refunds</h2>
          <ul>
            <li>Tokens included with your subscription are non-refundable</li>
            <li>Additional token purchases are non-refundable</li>
            <li>Unused tokens do not roll over (unless specified in your plan) and are not eligible for refunds</li>
            <li>If technical issues on our end prevent you from using purchased tokens, please contact support for credit or resolution</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Marketplace Services</h2>
          
          <h3>4.1 Platform Role</h3>
          <p>
            {companyName} acts as a platform connecting customers with independent vendors. 
            Refund policies for marketplace services are primarily determined by the vendor, 
            subject to our platform guidelines.
          </p>

          <h3>4.2 Service Not Delivered</h3>
          <p>
            If a vendor fails to deliver the purchased service within the agreed timeframe:
          </p>
          <ul>
            <li>Contact our support team immediately</li>
            <li>We will attempt to resolve the issue with the vendor</li>
            <li>If resolution is not possible, you may be eligible for a full refund minus any platform fees</li>
          </ul>

          <h3>4.3 Service Not As Described</h3>
          <p>
            If the delivered service significantly differs from what was described:
          </p>
          <ul>
            <li>You must notify us within 7 days of delivery</li>
            <li>Provide documentation of the discrepancy</li>
            <li>We will review your claim and may issue a partial or full refund</li>
          </ul>

          <h3>4.4 Platform Fees</h3>
          <p>
            Platform fees charged by {companyName} are generally non-refundable. However, in 
            cases where we determine the vendor failed to provide the service, platform fees 
            may be refunded at our discretion.
          </p>

          <h3>4.5 Dispute Resolution</h3>
          <p>
            For marketplace disputes:
          </p>
          <ol>
            <li>First, attempt to resolve the issue directly with the vendor</li>
            <li>If unsuccessful, submit a dispute to our support team within 14 days of delivery</li>
            <li>We will review the dispute and make a determination within 10 business days</li>
            <li>Our decision is final for disputes involving platform fees and refunds</li>
          </ol>
        </section>

        <section className="legal-section">
          <h2>5. Corporate Products</h2>
          
          <h3>5.1 Custom Products</h3>
          <p>
            Custom-branded merchandise and promotional items:
          </p>
          <ul>
            <li>Custom products are made to order and generally non-refundable</li>
            <li>Ensure all details are correct before confirming your order</li>
            <li>Proofs must be approved before production begins</li>
          </ul>

          <h3>5.2 Defective Products</h3>
          <p>
            If you receive defective products:
          </p>
          <ul>
            <li>Report the issue within 7 days of delivery</li>
            <li>Provide photos of the defects</li>
            <li>We will replace defective items or issue a refund at our discretion</li>
          </ul>

          <h3>5.3 Wrong Items</h3>
          <p>
            If you receive the wrong items:
          </p>
          <ul>
            <li>Contact us within 7 days of delivery</li>
            <li>We will arrange for the correct items to be sent</li>
            <li>Return shipping for incorrect items will be covered by us</li>
          </ul>

          <h3>5.4 Shipping Damage</h3>
          <p>
            For items damaged during shipping:
          </p>
          <ul>
            <li>Document the damage with photos upon receipt</li>
            <li>Report within 3 days of delivery</li>
            <li>We will file a claim with the carrier and arrange replacement or refund</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. How to Request a Refund</h2>
          <p>To request a refund:</p>
          <ol>
            <li>Email us at <a href={`mailto:${companyEmail}`}>{companyEmail}</a> with subject "Refund Request"</li>
            <li>Include your account email and order/subscription ID</li>
            <li>Explain the reason for your refund request</li>
            <li>Attach any relevant documentation or screenshots</li>
          </ol>
          <p>
            We aim to respond to all refund requests within 2 business days.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Refund Processing</h2>
          <ul>
            <li>Approved refunds will be processed within 5-10 business days</li>
            <li>Refunds will be issued to the original payment method</li>
            <li>Depending on your bank or credit card company, it may take additional time for the refund to appear on your statement</li>
            <li>You will receive an email confirmation when your refund is processed</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Non-Refundable Items</h2>
          <p>The following are generally non-refundable:</p>
          <ul>
            <li>Free tier accounts</li>
            <li>Services already rendered and completed to satisfaction</li>
            <li>Custom products after proof approval</li>
            <li>Token purchases after tokens have been used</li>
            <li>Promotional or discounted subscriptions (unless specified)</li>
            <li>Add-on purchases after delivery</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Exceptions</h2>
          <p>
            We understand that exceptional circumstances arise. If you believe your 
            situation warrants special consideration, please contact us. We review 
            exceptional cases individually and may make accommodations at our discretion.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Changes to This Policy</h2>
          <p>
            We reserve the right to modify this Refund Policy at any time. Changes will 
            be effective immediately upon posting. The "Last Updated" date at the top 
            indicates when the policy was last revised.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Contact Us</h2>
          <p>For refund inquiries or requests, please contact:</p>
          <ul className="contact-info">
            <li><strong>Email:</strong> {companyEmail}</li>
            <li><strong>Subject Line:</strong> Refund Request - [Your Order/Subscription ID]</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
